import React, { useCallback, useMemo, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { OutreachShell } from '../components/OutreachShell';
import { useCampaign } from '../hooks/useCampaign';
import { useCampaignActions, CampaignGoal } from '../hooks/useCampaignActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useReleaseOptions } from '../hooks/useReleaseOptions';
import { outreachCampaignPath } from '../outreachPaths';
import { outreachMessages } from '../outreachMessages';
import { BuilderBody } from './BuilderBody';
import { BuilderNav } from './BuilderNav';
import { StepSongGoal } from './StepSongGoal';
import { BUILDER_STEPS, BuilderStep } from './builderSteps';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

const INTRO: Record<BuilderStep, MessageDescriptor> = {
  song: messages.intro,
  who: messages.intro,
  message: messages.introMessage,
  sendFrom: messages.introSendFrom,
  start: messages.introStart,
};

interface CampaignBuilderScreenProps {
  accountId: number;
  initialSong: string | null;
  campaignUuid: string | null;
  /** The step that the draft opens on (`?step=`). */
  initialStep?: BuilderStep | null;
}

/**
 * The campaign setup (design outreach-v2 b to f): song and goal, who Plug contacts, message, send
 * from, start. Step 1 makes the draft; every later step saves on change. A running campaign opens
 * here too ("Edit setup", design g1): the song is locked, the other steps save with
 * `campaigns.update`, and the Start step says "Save changes". A done campaign opens its own page.
 * The page sits under Pitch (navigation v2): the crumb leads back.
 */
const CampaignBuilderScreenComponent: React.FC<CampaignBuilderScreenProps> = ({
  accountId,
  initialSong,
  campaignUuid,
  initialStep = null,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const navigate = useNavigate();
  const plan = useOutreachPlan();
  const [, setSearchParams] = useSearchParams();
  const { options, isLoading: isLoadingSongs } = useReleaseOptions(accountId);
  const { campaign, isLoading, error, retry } = useCampaign(accountId, campaignUuid);
  const actions = useCampaignActions(accountId);
  const [createFailed, setCreateFailed] = useState(false);
  const step: BuilderStep = campaignUuid === null ? 'song' : (initialStep ?? 'who');
  const isEditing = campaign !== undefined && campaign.status !== 'draft';

  const setStep = useCallback(
    (next: BuilderStep) => {
      if (campaignUuid !== null) {
        setSearchParams({ campaign: campaignUuid, step: next }, { replace: true });
      }
    },
    [campaignUuid, setSearchParams]
  );
  const cancel = useCallback(() => {
    if (isEditing && campaignUuid !== null) {
      // eslint-disable-next-line no-void
      void navigate(outreachCampaignPath(campaignUuid));
      return;
    }
    router.push(SMLRoutes.outreach);
  }, [campaignUuid, isEditing, navigate, router]);

  const create = useCallback(
    (catalogItemUuid: string, goal: CampaignGoal) => {
      setCreateFailed(false);
      const song = options.find((option) => option.catalogItemUuid === catalogItemUuid);
      actions
        .createCampaign({
          catalogItemUuid,
          goal,
          ...(song === undefined ? {} : { name: intl.formatMessage(messages.campaignName, { song: song.title }) }),
        })
        .then(({ campaignUuid: uuid }) => setSearchParams({ campaign: uuid, step: 'who' }, { replace: true }))
        .catch(() => setCreateFailed(true));
    },
    [actions, intl, options, setSearchParams]
  );

  const songUuid = campaign?.catalogItemUuid ?? initialSong;
  const song = useMemo(
    () => options.find((option) => option.catalogItemUuid === songUuid) ?? null,
    [options, songUuid]
  );

  const content = () => {
    if (isLoadingSongs || (campaignUuid !== null && isLoading)) {
      return <LoadingView />;
    }
    if (campaignUuid === null) {
      return (
        <div className={`${styles.layout} ${styles.layoutNoSum}`}>
          <BuilderNav step="song" onStep={() => undefined} locked />
          <div className={styles.stepCol}>
            <StepSongGoal
              accountId={accountId}
              options={options}
              initialSong={initialSong}
              onNext={create}
              onCancel={cancel}
              isSaving={actions.isSaving}
              failed={createFailed ? 'create' : null}
              disabled={plan.isDemo}
            />
          </div>
        </div>
      );
    }
    if (error !== null || campaign === undefined) {
      return <ErrorView error={error ?? undefined} retry={retry} />;
    }
    if (campaign.status === 'done') {
      return <Navigate to={outreachCampaignPath(campaign.campaignUuid)} replace />;
    }
    return (
      <BuilderBody
        accountId={accountId}
        campaign={campaign}
        songOptions={options}
        readOnly={plan.isDemo}
        isEditing={isEditing}
        step={step}
        onStep={setStep}
        onCancel={cancel}
      />
    );
  };

  return (
    <OutreachShell
      section="under"
      title={
        song === null
          ? intl.formatMessage(messages.newTitleNoSong)
          : intl.formatMessage(isEditing ? messages.editTitle : messages.newTitle, { song: song.title })
      }
      subtitle={intl.formatMessage(INTRO[step])}
      crumbs={[
        intl.formatMessage(outreachMessages.crumbPitch),
        intl.formatMessage(isEditing ? messages.crumbEdit : messages.crumb),
      ]}
    >
      <span className={`${shared.eyebrow} d-md-none`}>
        {intl.formatMessage(messages.stepOf, { step: BUILDER_STEPS.indexOf(step) + 1, total: BUILDER_STEPS.length })}
      </span>
      {content()}
    </OutreachShell>
  );
};

export const CampaignBuilderScreen = TypedMemo(CampaignBuilderScreenComponent);
